import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { getProvider } from '@publishweaver/ai-providers';
import { useAiSettings } from '@/hooks/useAiSettings';
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };
  const [, frontmatter, body] = match;
  const data: Record<string, unknown> = {};
  for (const line of (frontmatter ?? '').split('\n')) {
    const colon = line.indexOf(':');
    if (colon === -1) continue;
    const key = line.slice(0, colon).trim();
    const val = line.slice(colon + 1).trim();
    if (val.startsWith('[') && val.endsWith(']')) {
      data[key] = val.slice(1, -1).split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
    } else {
      data[key] = val.replace(/^['"]|['"]$/g, '');
    }
  }
  return { data, content: body ?? '' };
}
import { useAdminSession } from '@/hooks/useAdminSession';
import { adminApi, type PostSummary } from '@/lib/adminApi';
import type { BlogPostDraft } from '@publishweaver/ai-providers';
import { PostEditorPage } from './PostEditorPage';
import { StudioSettingsPage } from './StudioSettingsPage';
import { GeneratePostPage } from './GeneratePostPage';
import { buildSlug } from '@/lib/postContent';

type View =
  | { type: 'dashboard' }
  | { type: 'create'; prefill?: Partial<PrefillData> }
  | { type: 'edit'; slug: string }
  | { type: 'settings' }
  | { type: 'generate' };

interface PrefillData {
  title: string;
  slug: string;
  description: string;
  category: string;
  tags: string;
  content: string;
}

interface Props {
  onSignOut: () => void;
}

const STATUS_LABEL: Record<PostSummary['status'], string> = {
  draft: 'Draft',
  published: 'Published',
  deleted: 'Deleted',
};

export const AdminDashboard = ({ onSignOut }: Props) => {
  const { token } = useAdminSession();
  const { settings, toProviderConfig } = useAiSettings();
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [view, setView] = useState<View>({ type: 'dashboard' });
  const [trashOpen, setTrashOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [generatingMetadata, setGeneratingMetadata] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPosts = () => {
    if (!token) return;
    adminApi.listPosts(token).then(({ posts: p }) => { setPosts(p); }).catch(console.error);
  };

  useEffect(() => { loadPosts(); }, [token]);

  const handleDelete = (slug: string) => {
    if (!token) return;
    adminApi.deletePost(token, slug)
      .then(() => { loadPosts(); })
      .catch(console.error);
  };

  const handlePublish = (slug: string) => {
    if (!token) return;
    adminApi.updatePost(token, slug, { status: 'published' })
      .then(() => { loadPosts(); })
      .catch(console.error);
  };

  const handleRestore = (slug: string) => {
    if (!token) return;
    adminApi.updatePost(token, slug, { status: 'draft' })
      .then(() => { loadPosts(); })
      .catch(console.error);
  };

  const handlePermanentDelete = (slug: string) => {
    if (!token) return;
    adminApi.permanentlyDeletePost(token, slug)
      .then(() => { setConfirmDelete(null); loadPosts(); })
      .catch(console.error);
  };

  const handleImportFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = reader.result;
      if (typeof raw !== 'string') return;
      const { data, content } = parseFrontmatter(raw);
      const rawTags = data['tags'];
      const trimmedContent = content.trim();
      const existingTitle    = String(data['title']       ?? '');
      const existingSlug     = String(data['slug']        ?? '');
      const existingDesc     = String(data['description'] ?? '');
      const existingCategory = String(data['category']    ?? '');
      const existingTags     = Array.isArray(rawTags) ? rawTags.join(', ') : String(rawTags ?? '');

      const metadataMissing = !existingTitle || !existingDesc || !existingCategory;

      if (metadataMissing && trimmedContent) {
        setGeneratingMetadata(true);
        try {
          const provider = getProvider(settings.providerId);
          const draft = await provider.generateBlogPost({ content: trimmedContent, config: toProviderConfig() });
          setView({
            type: 'create',
            prefill: {
              title:       existingTitle    || draft.title,
              slug:        buildSlug(existingSlug || existingTitle || draft.slug),
              description: existingDesc     || draft.description,
              category:    existingCategory || draft.category,
              tags:        existingTags     || draft.tags.join(', '),
              content:     trimmedContent,
            },
          });
        } catch {
          setView({
            type: 'create',
            prefill: {
              title: existingTitle, slug: buildSlug(existingSlug || existingTitle),
              description: existingDesc, category: existingCategory,
              tags: existingTags, content: trimmedContent,
            },
          });
        } finally {
          setGeneratingMetadata(false);
        }
      } else {
        setView({
          type: 'create',
          prefill: {
            title:       existingTitle,
            slug:        buildSlug(existingSlug || existingTitle),
            description: existingDesc,
            category:    existingCategory,
            tags:        existingTags,
            content:     trimmedContent,
          },
        });
      }
    };
    reader.readAsText(file);
  };

  const handleGenerated = (draft: BlogPostDraft) => {
    setView({
      type: 'create',
      prefill: {
        title: draft.title,
        slug: buildSlug(draft.slug),
        description: draft.description,
        category: draft.category,
        tags: draft.tags.join(', '),
        content: draft.content,
      },
    });
  };

  if (view.type === 'settings') {
    return <StudioSettingsPage onBack={() => { setView({ type: 'dashboard' }); }} />;
  }

  if (view.type === 'generate') {
    return (
      <GeneratePostPage
        onGenerated={handleGenerated}
        onCancel={() => { setView({ type: 'dashboard' }); }}
      />
    );
  }

  if (view.type === 'create') {
    return (
      <PostEditorPage
        prefill={view.prefill}
        onSuccess={() => { loadPosts(); setView({ type: 'dashboard' }); }}
        onCancel={() => { setView({ type: 'dashboard' }); }}
      />
    );
  }

  if (view.type === 'edit') {
    return (
      <PostEditorPage
        slug={view.slug}
        onSuccess={() => { loadPosts(); setView({ type: 'dashboard' }); }}
        onCancel={() => { setView({ type: 'dashboard' }); }}
      />
    );
  }

  const activePosts = posts.filter((p) => p.status !== 'deleted');
  const trashedPosts = posts.filter((p) => p.status === 'deleted');

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <span className="admin-topbar__brand">Studio</span>
        <div className="admin-topbar__actions">
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => { setView({ type: 'settings' }); }}
          >
            Settings
          </button>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <main className="admin-main">
        {/* ── Active posts ─────────────────────────────────────────────── */}
        <div className="admin-section-header">
          <h2 className="admin-section-title">Posts</h2>
          <div className="admin-section-header__actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,.mdx"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              disabled={generatingMetadata}
              onClick={() => { fileInputRef.current?.click(); }}
            >
              {generatingMetadata ? 'Generating metadata…' : 'Import .md'}
            </button>
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => { setView({ type: 'generate' }); }}
            >
              Generate
            </button>
            <button className="admin-btn" onClick={() => { setView({ type: 'create' }); }}>
              + New post
            </button>
          </div>
        </div>

        {activePosts.length === 0 ? (
          <p className="admin-status">No posts yet.</p>
        ) : (
          <ul className="admin-post-list">
            {activePosts.map((post) => (
              <li key={post.slug} className="admin-post-item">
                <div className="admin-post-item__info">
                  <span className="admin-post-item__title">{post.title}</span>
                  <span className="admin-post-item__slug">{post.slug}</span>
                </div>
                <div className="admin-post-item__actions">
                  <span className={`admin-badge admin-badge--${post.status}`}>
                    {STATUS_LABEL[post.status]}
                  </span>
                  {post.status === 'draft' && (
                    <button
                      className="admin-btn admin-btn--sm"
                      onClick={() => { handlePublish(post.slug); }}
                    >
                      Publish
                    </button>
                  )}
                  <button
                    className="admin-btn admin-btn--ghost admin-btn--sm"
                    onClick={() => { setView({ type: 'edit', slug: post.slug }); }}
                  >
                    Edit
                  </button>
                  <button
                    className="admin-btn admin-btn--danger admin-btn--sm"
                    onClick={() => { handleDelete(post.slug); }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* ── Trash ────────────────────────────────────────────────────── */}
        {trashedPosts.length > 0 && (
          <div className="admin-trash">
            <button
              className="admin-trash__toggle"
              onClick={() => { setTrashOpen((o) => !o); }}
            >
              <span>Trash</span>
              <span className="admin-trash__count">{trashedPosts.length}</span>
              <span className="admin-trash__arrow">{trashOpen ? '▲' : '▼'}</span>
            </button>

            {trashOpen && (
              <ul className="admin-post-list admin-post-list--trash">
                {trashedPosts.map((post) => (
                  <li key={post.slug} className="admin-post-item admin-post-item--deleted">
                    <div className="admin-post-item__info">
                      <span className="admin-post-item__title">{post.title}</span>
                      <span className="admin-post-item__slug">{post.slug}</span>
                    </div>
                    <div className="admin-post-item__actions">
                      <span className="admin-badge admin-badge--deleted">Deleted</span>
                      <button
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        onClick={() => { handleRestore(post.slug); }}
                      >
                        Restore
                      </button>
                      <button
                        className="admin-btn admin-btn--danger admin-btn--sm"
                        onClick={() => { setConfirmDelete(post.slug); }}
                      >
                        Delete permanently
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>

      {/* ── Permanent delete confirmation ─────────────────────────────── */}
      {confirmDelete !== null && (
        <div className="admin-overlay">
          <div className="admin-dialog">
            <p className="admin-dialog__body">
              Permanently delete <strong>{confirmDelete}</strong>? This cannot be undone.
            </p>
            <div className="admin-dialog__actions">
              <button
                className="admin-btn admin-btn--danger"
                onClick={() => { handlePermanentDelete(confirmDelete); }}
              >
                Delete permanently
              </button>
              <button
                className="admin-btn admin-btn--ghost"
                onClick={() => { setConfirmDelete(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
